import { useEffect, useState } from 'react';
import { Check, Circle } from '@phosphor-icons/react';
import { ChecklistItem } from '@/hooks/useOnboardingChecklist';

const monoFont = "'IBM Plex Mono', monospace";

interface GettingStartedProps {
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  siteName: string;
  onNavigate: (tab: string) => void;
  onOpenTutorial: () => void;
}

export function GettingStarted({ items, completedCount, totalCount, siteName, onNavigate, onOpenTutorial }: GettingStartedProps) {
  const pct = Math.round((completedCount / totalCount) * 100);
  const firstPendingIdx = items.findIndex(i => !i.done);

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', fontFamily: monoFont }}>
      {/* Header */}
      <div style={{ marginBottom: 48 }}>
        <h1 style={{ fontSize: 24, fontWeight: 300, color: '#F7F6F2', margin: 0 }}>
          Olá, {siteName || 'usuário'}.
        </h1>
        <p style={{ fontSize: 16, fontWeight: 300, color: 'rgba(247,246,242,0.5)', marginTop: 8 }}>
          Vamos configurar seu site.
        </p>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>
            {completedCount} de {totalCount} concluídos
          </span>
          <span style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>
            {pct}%
          </span>
        </div>
        <div style={{ width: '100%', height: 2, backgroundColor: 'rgba(247,246,242,0.1)' }}>
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              backgroundColor: '#F7F6F2',
              transition: 'width 0.5s ease',
            }}
          />
        </div>
      </div>

      {/* Task list */}
      <div>
        {items.map((item, idx) => {
          const isActive = idx === firstPendingIdx;
          return (
            <div
              key={item.key}
              style={{
                padding: '20px 24px',
                borderBottom: '0.5px solid rgba(247,246,242,0.06)',
                backgroundColor: isActive && !item.done ? 'rgba(247,246,242,0.04)' : 'transparent',
                borderLeft: isActive && !item.done ? '2px solid #F7F6F2' : '2px solid transparent',
                transition: 'all 0.3s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                {/* Icon */}
                <div style={{ marginTop: 2, flexShrink: 0 }}>
                  {item.done ? (
                    <Check size={16} strokeWidth={1.5} style={{ color: '#00E87A', transition: 'opacity 0.3s ease' }} />
                  ) : (
                    <Circle size={16} strokeWidth={1} style={{ color: 'rgba(247,246,242,0.2)' }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 300,
                      color: item.done ? 'rgba(247,246,242,0.4)' : '#F7F6F2',
                      textDecoration: item.done ? 'line-through' : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {item.title}
                  </span>

                  {!item.done && (
                    <>
                      <p style={{ fontSize: 12, color: 'rgba(247,246,242,0.5)', marginTop: 4, marginBottom: 0 }}>
                        {item.description}
                      </p>
                      {item.action && (
                        <button
                          onClick={() => onNavigate(item.action!.tab)}
                          style={{
                            fontFamily: monoFont,
                            fontSize: 12,
                            fontWeight: 300,
                            color: 'rgba(247,246,242,0.5)',
                            border: '0.5px solid rgba(247,246,242,0.2)',
                            backgroundColor: 'transparent',
                            padding: '6px 12px',
                            marginTop: 12,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            textTransform: 'uppercase',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.borderColor = 'rgba(247,246,242,0.6)';
                            e.currentTarget.style.color = '#F7F6F2';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.borderColor = 'rgba(247,246,242,0.2)';
                            e.currentTarget.style.color = 'rgba(247,246,242,0.5)';
                          }}
                        >
                          {item.action.label}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 48 }}>
        <button
          onClick={onOpenTutorial}
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: 'rgba(247,246,242,0.25)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.5)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.25)')}
        >
          Precisa de ajuda? Acesse o Tutorial →
        </button>
      </div>
    </div>
  );
}
